import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ShieldCheck, ShieldOff, ChevronRight, Loader2, KeyRound } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ProfileTabs, ReadonlyRow, StateChip } from '@/components/chassis/ProfileTabs';

import { useAuthStore } from '@/stores/auth.store';
import { mfaApi } from '@/features/auth/mfa.api';
import { meApi } from '@/features/me/me.api';
import { useProfileSummary } from '@/features/agent-progress/progressHooks';
import { toast } from '@/stores/toast.store';
import { ApiError } from '@/lib/api-client';
import { useT } from '@/i18n';

const MFA_REQUIRED_ROLES = ['admin', 'supervisor'];

/**
 * F5-T2-5 (mockup «Agente 04», ventana 6) — perfil y ajustes como familia de
 * pestañas: Datos · Seguridad · Mis números. Un primario por pestaña; estado
 * por ETIQUETA (chip), no por color de fondo; lo no-editable sin campo y con
 * la razón al lado; las advertencias EN el punto de acción. La tercera
 * pestaña cambia por rol: el agente ve sus números (profile-summary); los
 * demás roles no la tienen (su versión llega con sus tandas).
 * La lógica de MFA es la preexistente, movida a la pestaña Seguridad sin
 * cambios de comportamiento.
 */
export function SettingsPage(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [tab, setTab] = useState('datos');
  const [showDisable, setShowDisable] = useState(false);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwNew2, setPwNew2] = useState('');

  const isAgent = user?.role === 'agent';
  const summary = useProfileSummary();

  const disable = useMutation({
    mutationFn: () => mfaApi.disable(password, code),
    onSuccess: () => {
      const current = useAuthStore.getState().user;
      if (current) {
        useAuthStore.setState({ user: { ...current, mfaEnabled: false } });
      }
      toast.success('MFA disabled', 'You can re-enable it any time.');
      setShowDisable(false);
      setPassword('');
      setCode('');
    },
  });

  const changePw = useMutation({
    mutationFn: () => meApi.changePassword({ currentPassword: pwCurrent, newPassword: pwNew }),
    onSuccess: () => {
      toast.success(t('settings.security.pwChanged'));
      setPwCurrent('');
      setPwNew('');
      setPwNew2('');
    },
  });

  if (!user) {
    return (
      <Alert variant="error">
        <AlertTitle>Not signed in</AlertTitle>
        <AlertDescription>Please sign in again.</AlertDescription>
      </Alert>
    );
  }

  const mfaRequired = MFA_REQUIRED_ROLES.includes(user.role);
  const tabs = [
    { id: 'datos', label: t('settings.tabs.datos') },
    { id: 'seguridad', label: t('settings.tabs.seguridad') },
    ...(isAgent ? [{ id: 'numeros', label: t('settings.tabs.numeros') }] : []),
  ];
  const canSubmitPw =
    pwCurrent.length > 0 && pwNew.length >= 8 && pwNew === pwNew2 && !changePw.isPending;

  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="text-3xl font-semibold text-ink-900 tracking-tight leading-tight">
        {t('settings.title')}
      </h1>

      <ProfileTabs tabs={tabs} active={tab} onTab={setTab} />

      {tab === 'datos' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('settings.datos.title')}</CardTitle>
            <CardDescription>{t('settings.datos.desc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-ink-100">
              <ReadonlyRow label={t('settings.datos.name')} value={user.fullName} />
              <ReadonlyRow
                label={t('settings.datos.email')}
                value={user.email}
                reason={t('settings.datos.emailReason')}
              />
              <ReadonlyRow
                label={t('settings.datos.role')}
                value={user.role}
                reason={t('settings.datos.roleReason')}
              />
              <ReadonlyRow
                label={t('settings.datos.languages')}
                value={user.languages.join(', ') || '—'}
              />
              {user.mustChangePassword && (
                <ReadonlyRow
                  label={t('settings.datos.password')}
                  value={<StateChip>{t('settings.datos.mustChange')}</StateChip>}
                />
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {tab === 'seguridad' && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-ink-500" />
                {t('settings.security.pwTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="pw-cur">{t('settings.security.pwCurrent')}</Label>
                <Input
                  id="pw-cur"
                  type="password"
                  autoComplete="current-password"
                  value={pwCurrent}
                  onChange={(e) => setPwCurrent(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pw-new">{t('settings.security.pwNew')}</Label>
                <Input
                  id="pw-new"
                  type="password"
                  autoComplete="new-password"
                  value={pwNew}
                  onChange={(e) => setPwNew(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pw-new2">{t('settings.security.pwNew2')}</Label>
                <Input
                  id="pw-new2"
                  type="password"
                  autoComplete="new-password"
                  value={pwNew2}
                  onChange={(e) => setPwNew2(e.target.value)}
                />
              </div>
              {changePw.error && (
                <Alert variant="error" role="alert">
                  <AlertTitle>{t('settings.security.pwFailed')}</AlertTitle>
                  <AlertDescription>
                    {changePw.error instanceof ApiError
                      ? changePw.error.message
                      : (changePw.error as Error).message}
                  </AlertDescription>
                </Alert>
              )}
              {/* La advertencia EN el punto de accion (mockup v.6) — y el
                  detalle propio del rol agente. */}
              <p className="text-xs text-ink-500" data-t2="pw-warning">
                {t('settings.security.pwWarning')}
                {isAgent ? ` ${t('settings.security.pwWarningAgent')}` : ''}
              </p>
              <div className="flex justify-end">
                <Button onClick={() => changePw.mutate()} disabled={!canSubmitPw}>
                  {changePw.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t('settings.security.pwSubmit')}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {user.mfaEnabled ? (
                  <ShieldCheck className="h-5 w-5 text-mint-700" />
                ) : (
                  <ShieldOff className="h-5 w-5 text-peach-700" />
                )}
                Multi-factor authentication
              </CardTitle>
              <CardDescription>
                {user.mfaEnabled
                  ? 'Active. You\'ll be asked for a code at every sign-in.'
                  : mfaRequired
                    ? 'Required for your role. Enable it now to keep access.'
                    : 'Optional but recommended.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {user.mfaEnabled ? (
                <>
                  {mfaRequired && (
                    <Alert variant="info">
                      <AlertTitle>MFA is mandatory for {user.role}</AlertTitle>
                      <AlertDescription>
                        You can rotate to a new device by disabling and re-enabling, but the disable
                        request will be rejected for your role until policy changes.
                      </AlertDescription>
                    </Alert>
                  )}
                  {!showDisable && (
                    <Button
                      variant="ghost"
                      onClick={() => setShowDisable(true)}
                      disabled={mfaRequired}
                    >
                      Disable MFA
                    </Button>
                  )}

                  {showDisable && !mfaRequired && (
                    <div className="space-y-3 border-t border-ink-100 pt-4">
                      <p className="text-sm text-ink-700">
                        Confirm your password and a current code to disable.
                      </p>
                      {disable.error && (
                        <Alert variant="error" role="alert">
                          <AlertTitle>Couldn't disable</AlertTitle>
                          <AlertDescription>
                            {disable.error instanceof ApiError
                              ? disable.error.message
                              : (disable.error as Error).message}
                          </AlertDescription>
                        </Alert>
                      )}
                      <div className="space-y-1.5">
                        <Label htmlFor="dpw">Password</Label>
                        <Input
                          id="dpw"
                          type="password"
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="dcode">Current 6-digit code</Label>
                        <Input
                          id="dcode"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={6}
                          value={code}
                          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="ghost" onClick={() => setShowDisable(false)}>
                          Cancel
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => disable.mutate()}
                          disabled={!password || code.length !== 6 || disable.isPending}
                        >
                          {disable.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                          Disable MFA
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <Button onClick={() => navigate('/settings/mfa/enroll')}>
                  Enable MFA
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {tab === 'numeros' && isAgent && (
        <Card>
          <CardHeader>
            <CardTitle>{t('settings.numeros.title')}</CardTitle>
            <CardDescription>{t('settings.numeros.desc')}</CardDescription>
          </CardHeader>
          <CardContent>
            {summary.isLoading ? (
              <p className="text-sm text-ink-500">…</p>
            ) : summary.data ? (
              <div className="divide-y divide-ink-100">
                <ReadonlyRow
                  label={t('settings.numeros.today')}
                  value={`${summary.data.today.screeningsToday} / ${summary.data.today.targetToday}`}
                  reason={summary.data.today.hitTarget ? t('settings.numeros.hit') : undefined}
                />
                <ReadonlyRow
                  label={t('settings.numeros.month')}
                  value={String(summary.data.monthly.screeningsMonth)}
                />
                <ReadonlyRow
                  label={t('settings.numeros.tier')}
                  value={<StateChip>{summary.data.monthly.tierDisplayed}</StateChip>}
                />
                <ReadonlyRow
                  label={t('settings.numeros.streak')}
                  value={String(summary.data.streak.daysWorking)}
                />
              </div>
            ) : (
              <p className="text-sm text-ink-500">{t('settings.numeros.unavailable')}</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
